import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type DatasetKey, datasetKeys } from '@infinex/types';

import {
  DEFAULT_PANEL_WIDTH,
  MIN_PANEL_WIDTH,
  type PanelMode,
  REFRESH_INTERVAL_MS,
  WS_STATUS,
} from './constants';
import { DatasetRow } from './DatasetRow';
import { CloseIcon, FullscreenIcon } from './icons';
import { SettingsPopover } from './SettingsPopover';

import { useSession } from '../../session';
import { useDebugMode } from '../hooks/useDebugMode';
import { useLocalStorage } from '../hooks/useLocalStorage';
import type { DatasetExtraTabs, DebuggerData } from '../types';

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════

export function DatasetsDebugger({
  getDebuggerData,
  extraDatasetTabs,
  customHeaderActions,
}: {
  /** Returns enriched dataset state; only called while the panel is open */
  getDebuggerData: () => DebuggerData;
  /** Extra tabs rendered inside an expanded dataset row */
  extraDatasetTabs?: DatasetExtraTabs;
  /** Additional actions to display in the header bar */
  customHeaderActions?: ReactNode;
}) {
  const isDebugMode = useDebugMode();

  // Panel state — NOT persisted by default
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debuggerData, setDebuggerData] = useState<DebuggerData>({
    datasets: {},
    wsStatus: 'disconnected',
    fallbackPollIntervalMs: false,
    markDatasetUpdated: () => {},
  });
  const [now, setNow] = useState(Date.now);

  const { session } = useSession();

  // ── Dataset ordering (drag-to-reorder, persisted) ──
  const [savedOrder, setSavedOrder] = useLocalStorage<DatasetKey[]>({
    key: 'dataset-order',
    namespace: 'datasets-debugger',
    initialValue: [...datasetKeys],
  });

  // State drives renders; refs let the pointermove handler read latest values
  // without stale closures (the handler is registered once on pointerdown).
  const [liveOrder, setLiveOrder] = useState<DatasetKey[] | null>(null);
  const [draggedKey, setDraggedKey] = useState<DatasetKey | null>(null);
  const liveOrderRef = useRef<DatasetKey[] | null>(null);
  const draggedKeyRef = useRef<DatasetKey | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const handleReorderStart = useCallback(
    (key: DatasetKey) => {
      const order = savedOrder ? [...savedOrder] : [...datasetKeys];
      liveOrderRef.current = order;
      draggedKeyRef.current = key;
      setLiveOrder(order);
      setDraggedKey(key);
    },
    [savedOrder]
  );

  // Stable callback (no state deps) — reads everything from refs
  const handleReorderMove = useCallback((clientY: number) => {
    const order = liveOrderRef.current;
    const key = draggedKeyRef.current;
    if (!key || !listRef.current || !order) return;

    // Each child has data-dataset-key; find which one the pointer is over
    const children = Array.from(listRef.current.children) as HTMLElement[];
    let targetKey: DatasetKey | null = null;
    let insertAfter = false;

    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      const childKey = children[i].dataset.datasetKey as DatasetKey | undefined;
      if (!childKey) continue;

      if (clientY < rect.top + rect.height / 2) {
        targetKey = childKey;
        break;
      }
      // Past the midpoint of the last visible child → insert after it
      if (i === children.length - 1) {
        targetKey = childKey;
        insertAfter = true;
      }
    }

    if (!targetKey || targetKey === key) return;

    const fromIdx = order.indexOf(key);
    const toBaseIdx = order.indexOf(targetKey);
    if (fromIdx === -1 || toBaseIdx === -1) return;

    const toIdx = insertAfter
      ? toBaseIdx + (fromIdx > toBaseIdx ? 1 : 1)
      : toBaseIdx;
    if (fromIdx === toIdx) return;

    const next = [...order];
    next.splice(fromIdx, 1);
    // Adjust target index if dragging downward (removal shifted indices)
    const adjustedTo = fromIdx < toIdx ? toIdx - 1 : toIdx;
    next.splice(adjustedTo, 0, key);

    liveOrderRef.current = next;
    setLiveOrder(next);
  }, []);

  const handleReorderEnd = useCallback(() => {
    if (liveOrderRef.current) {
      setSavedOrder(liveOrderRef.current);
    }
    liveOrderRef.current = null;
    draggedKeyRef.current = null;
    setLiveOrder(null);
    setDraggedKey(null);
  }, [setSavedOrder]);

  // ── Panel sizing ──
  const [panelMode, setPanelMode] = useLocalStorage<PanelMode>({
    key: 'panel-mode',
    namespace: 'datasets-debugger',
    initialValue: 'windowed',
  });

  const [panelWidth, setPanelWidth] = useLocalStorage<number>({
    key: 'panel-width',
    namespace: 'datasets-debugger',
    initialValue: DEFAULT_PANEL_WIDTH,
  });

  const panelRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);

  // ── Settings: persist-on-refresh toggle ──
  const [isPersistEnabled, setIsPersistEnabled] = useLocalStorage<boolean>({
    key: 'persist-open',
    namespace: 'datasets-debugger',
    initialValue: false,
  });

  const [persistedOpen, setPersistedOpen] = useLocalStorage<boolean>({
    key: 'expanded',
    namespace: 'datasets-debugger',
    initialValue: false,
    supportSSR: true,
  });

  // Restore persisted open state once, after it hydrates from storage.
  // `persistedOpen` uses `supportSSR`, so it's `null` until the client-side
  // hydration effect runs; a mount-only effect would read that `null` and never
  // re-run, so we react to the hydrated value and guard it to fire once.
  const hasRestoredOpen = useRef(false);
  useEffect(() => {
    if (hasRestoredOpen.current || persistedOpen === null) return;
    hasRestoredOpen.current = true;
    if (isPersistEnabled && persistedOpen) {
      setIsOpen(true);
    }
  }, [persistedOpen, isPersistEnabled]);

  const handleToggleOpen = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (isPersistEnabled) {
        setPersistedOpen(open);
      }
    },
    [isPersistEnabled, setPersistedOpen]
  );

  // ── Drag-to-resize ──
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizing.current = true;
      const startX = e.clientX;
      const startWidth = panelWidth ?? DEFAULT_PANEL_WIDTH;

      const onMouseMove = (ev: MouseEvent) => {
        const delta = startX - ev.clientX;
        const newWidth = Math.max(
          MIN_PANEL_WIDTH,
          Math.min(window.innerWidth, startWidth + delta)
        );
        if (panelRef.current) {
          panelRef.current.style.width = `${newWidth}px`;
        }
      };

      const onMouseUp = (ev: MouseEvent) => {
        isResizing.current = false;
        const delta = startX - ev.clientX;
        const newWidth = Math.max(
          MIN_PANEL_WIDTH,
          Math.min(window.innerWidth, startWidth + delta)
        );
        setPanelWidth(newWidth);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [panelWidth, setPanelWidth]
  );

  // Refresh data + clock while open
  useEffect(() => {
    if (!isOpen) {
      setDebuggerData({
        datasets: {},
        wsStatus: 'disconnected',
        fallbackPollIntervalMs: false,
        markDatasetUpdated: () => {},
      });
      return;
    }

    setDebuggerData(getDebuggerData());
    setNow(Date.now());

    const id = setInterval(() => {
      setDebuggerData(getDebuggerData());
      setNow(Date.now());
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(id);
  }, [isOpen, getDebuggerData]);

  // Sort datasets by custom order (live order during drag, saved otherwise)
  const filteredKeys = useMemo(() => {
    const order = liveOrder ?? savedOrder ?? [...datasetKeys];
    const orderedKeys = [
      ...order.filter((k) => datasetKeys.includes(k)),
      ...datasetKeys.filter((k) => !order.includes(k)),
    ];
    return orderedKeys.filter((key) =>
      searchQuery ? key.toLowerCase().includes(searchQuery.toLowerCase()) : true
    );
  }, [searchQuery, savedOrder, liveOrder]);

  if (!isDebugMode) return null;

  // ── Collapsed tab ──
  if (!isOpen) {
    return (
      <div className="fixed top-1/2 right-px z-[9999] -mt-[46px]">
        <div className="w-7 rotate-90">
          <button
            className="bg-fillHighlight hover:bg-fillHighlightHover active:bg-fillHighlightPress text-highlightOnFill cursor-pointer rounded-lg rounded-t-none px-4 py-1 text-sm font-semibold"
            onClick={() => handleToggleOpen(true)}
            aria-label="Open datasets debugger"
          >
            Datasets
          </button>
        </div>
      </div>
    );
  }

  const isFullscreen = panelMode === 'fullscreen';
  const resolvedWidth = panelWidth ?? DEFAULT_PANEL_WIDTH;

  // ── Expanded panel ──
  return (
    <div
      ref={panelRef}
      className="bg-canvas fixed top-0 right-0 bottom-0 z-[9999] flex flex-col border-l"
      style={isFullscreen ? { left: 0 } : { width: resolvedWidth }}
    >
      {/* ─── Resize handle (windowed mode only) ─── */}
      {!isFullscreen ? (
        <div
          className="group absolute top-0 bottom-0 left-0 z-10 w-1.5 cursor-col-resize"
          onMouseDown={handleResizeStart}
        >
          <div className="absolute inset-y-0 left-0 w-0.5 bg-[var(--border-color-secondary)] opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
      ) : null}

      {/* ─── Header ─── */}
      <header className="bg-surface flex shrink-0 items-center gap-3 border-b px-4 py-2.5">
        <h2 className="text-emphasis text-base font-bold">Datasets</h2>
        <WsStatusBadge status={debuggerData.wsStatus} />

        <div className="ml-auto flex items-center gap-2">
          {customHeaderActions}

          <button
            className="text-secondary hover:text-emphasis hover:bg-fillHover cursor-pointer rounded-md p-1.5 transition-colors"
            onClick={() =>
              setPanelMode(isFullscreen ? 'windowed' : 'fullscreen')
            }
            aria-label={
              isFullscreen ? 'Switch to windowed' : 'Switch to fullscreen'
            }
          >
            <FullscreenIcon size={16} />
          </button>

          <SettingsPopover
            isPersistEnabled={!!isPersistEnabled}
            onTogglePersist={() => {
              const next = !isPersistEnabled;
              setIsPersistEnabled(next);
              if (!next) setPersistedOpen(false);
            }}
          />
          <button
            className="text-secondary hover:text-emphasis hover:bg-fillHover cursor-pointer rounded-md p-1.5 transition-colors"
            onClick={() => handleToggleOpen(false)}
            aria-label="Close datasets debugger"
          >
            <CloseIcon size={16} />
          </button>
        </div>
      </header>

      {/* ─── Session info ─── */}
      <div className="bg-surface shrink-0 border-b px-4 py-1.5 text-xs">
        {session ? (
          <div className="flex gap-4">
            <span className="text-secondary">
              Session:{' '}
              <span className="text-default font-mono">
                {session.sessionId}
              </span>
            </span>
            <span className="text-secondary">
              User:{' '}
              <span className="text-default font-mono">{session.userId}</span>
            </span>
          </div>
        ) : (
          <span className="text-tertiary">No active session</span>
        )}
      </div>

      {/* ─── Search ─── */}
      <div className="shrink-0 border-b px-4 py-2">
        <input
          type="text"
          placeholder="Search datasets…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-fill text-default placeholder:text-tertiary focus:border-info w-full rounded-lg border px-3 py-1.5 text-sm transition-colors outline-none"
        />
      </div>

      {/* ─── Dataset list ─── */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-2">
        {filteredKeys.length === 0 ? (
          <p className="text-tertiary py-8 text-center text-sm">
            {searchQuery
              ? `No datasets match "${searchQuery}"`
              : 'No datasets available'}
          </p>
        ) : (
          <div ref={listRef} className="flex flex-col gap-0.5">
            {filteredKeys.map((key) => (
              <DatasetRow
                key={key}
                datasetKey={key}
                info={debuggerData.datasets[key]}
                now={now}
                wsStatus={debuggerData.wsStatus}
                fallbackPollIntervalMs={debuggerData.fallbackPollIntervalMs}
                extraTabs={extraDatasetTabs}
                isDragging={draggedKey === key}
                isReordering={draggedKey !== null}
                onGrabStart={() => handleReorderStart(key)}
                onGrabMove={handleReorderMove}
                onGrabEnd={handleReorderEnd}
                onMarkUpdated={debuggerData.markDatasetUpdated}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// WebSocket Status Badge (kept here as it's a simple header-only element)
// ═══════════════════════════════════════════════════════════════════════════════

function WsStatusBadge({ status }: { status: DebuggerData['wsStatus'] }) {
  const config = WS_STATUS[status];
  return (
    <div className="bg-fill flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs">
      <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
      <span className="text-secondary">{config.label}</span>
    </div>
  );
}
